import { useCallback, type ReactElement } from 'react'
import { cn } from '@util'
import { isDefined, isNotDefined, isTrue, type PartialTeamData, type TeamData, type TeamLevel } from '@terros/common'
import { Loader } from '@mantine/core'
import { useToastError } from '@hooks'
import { useTeamCache, useUpdateTeam } from '@front/model'
import classes from '../hierarchyViewport.module.css'
import { isNodeActivationKey } from '../../util'
import { useTeamHierarchyContext } from '../../TeamHierarchyContext'
import { useTeamDrag } from '../../TeamDragContext'
import { toVisibilityFoldVariant, VisibilityCornerFold } from './VisibilityCornerFold'
import { ManageTeamButton } from './ManageTeamButton'
import { HiddenChildrenIndicator } from './HiddenChildrenIndicator'
import { TeamNodeContent } from './Content'

type TeamNodeProps = {
  team: TeamData
}

export const TeamNode = ({ team }: TeamNodeProps): ReactElement => {
  const { teams, selectTeam, canEdit, layout, visibleTeamIds } = useTeamHierarchyContext()
  const { dragState, startDrag, endDrag, setDragOverTeam, canDrop } = useTeamDrag()
  const showVisibility = isDefined(visibleTeamIds) && isNotDefined(dragState.draggedTeam)
  const isHiddenFromFocus = showVisibility && !visibleTeamIds.has(team.teamId)
  const isFocused = layout.focusTeam?.teamId === team.teamId
  const foldVariant = toVisibilityFoldVariant({
    isHidden: isHiddenFromFocus,
    isRestricted: showVisibility && isTrue(team.restrictVisibility),
    isSelected: isFocused,
  })
  const isDragging = dragState.draggedTeam?.teamId === team.teamId
  const isDragOver = dragState.dragOverTeam?.teamId === team.teamId
  const isValidDropTarget = canDrop(team)
  const cache = useTeamCache()
  const { update, isUpdating, error } = useUpdateTeam()
  useToastError(error)

  const onDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>): Promise<void> => {
      e.preventDefault()
      e.stopPropagation()
      const draggedTeam = dragState.draggedTeam
      if (isValidDropTarget && draggedTeam) {
        const newParentId = team.teamId
        const newLevel = (team.level - 1) as TeamLevel
        const teamToUpdate: PartialTeamData = {
          teamId: draggedTeam.teamId,
          parentId: newParentId,
          level: newLevel,
        }

        // optimistic update
        cache.addChildTeam(team.teamId, draggedTeam)
        if (draggedTeam.parentId) {
          cache.removeChildTeam(draggedTeam.parentId, draggedTeam.teamId)
        }
        cache.partialUpdateTeam(teamToUpdate)
        update(teamToUpdate).catch(() => {
          // revert optimistic update
          cache.partialUpdateTeam({ ...draggedTeam })
          if (draggedTeam.parentId) {
            cache.addChildTeam(draggedTeam.parentId, draggedTeam)
          }
          cache.removeChildTeam(team.teamId, draggedTeam.teamId)
        })
      }
      endDrag()
    },
    [isValidDropTarget, dragState.draggedTeam, endDrag, team.teamId, team.level, cache, update]
  )

  const onDragStart = useCallback((): void => {
    const highestChildLevel = teams
      .filter((t) => t.parentId === team.teamId)
      .reduce<TeamLevel | undefined>((max, t) => {
        if (max === undefined) return t.level
        return Math.max(max, t.level)
      }, undefined)

    startDrag(team, highestChildLevel)
  }, [teams, startDrag, team])

  const onDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault()
      e.stopPropagation()
      if (dragState.draggedTeam?.teamId !== team.teamId) setDragOverTeam(team)
    },
    [dragState.draggedTeam?.teamId, team, setDragOverTeam]
  )

  const onDragEnd = useCallback((): void => {
    endDrag()
  }, [endDrag])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>): void => {
      if (!isNodeActivationKey(e)) return
      e.preventDefault()
      selectTeam(team.teamId)
    },
    [selectTeam, team.teamId]
  )

  const onDragExit = useCallback((): void => {
    setDragOverTeam(null)
  }, [setDragOverTeam])

  return (
    <div className='gap-xs flex flex-col items-center select-none'>
      <div className='relative'>
        <div
          data-no-pan='true'
          data-node-id={team.teamId}
          draggable={canEdit}
          role='button'
          tabIndex={0}
          className={cn(
            'gap-xs relative flex min-h-[76px] max-w-[200px] min-w-[150px] flex-col items-center justify-center overflow-hidden rounded-md border border-neutral-200 px-10 py-4 shadow-xs transition-colors',
            canEdit ? 'cursor-grab' : 'cursor-pointer',
            !isFocused && 'hover:bg-neutral-light-hover focus-visible:bg-neutral-light-hover',
            isHiddenFromFocus && 'border-[3px] border-rose-400 text-[#737373]',
            isDragging && 'cursor-grabbing opacity-50',
            isDragOver && (isValidDropTarget ? 'border-emerald-filled bg-emerald-light' : 'border-rose-filled'),
            isFocused && classes.focusedNode
          )}
          onDragEnd={onDragEnd}
          onDragExit={onDragExit}
          onDragOver={onDragOver}
          onDragStart={onDragStart}
          onDrop={onDrop}
          onKeyDown={onKeyDown}
          onClick={() => selectTeam(team.teamId)}
        >
          {isUpdating && isDragging ? <Loader /> : <TeamNodeContent team={team} />}
          <ManageTeamButton team={team} />
        </div>
        {foldVariant && <VisibilityCornerFold variant={foldVariant} />}
      </div>
      <HiddenChildrenIndicator count={layout.hiddenChildCounts.get(team.teamId) ?? 0} />
    </div>
  )
}
